// DreamTeamLogo — the animated emblem for the staff-only Dream Team channel.
// Default (and other themes): the CSS "orbital atom" (pulsing core + 3 orbiting nodes).
// Spooky theme: the "Coven" emblem — a guardian head (skull → wizard → witch → zombie → crow)
// that morphs every 10s with a flip-and-fade, over drifting smoke + a purple glow. The border
// spirit-ghost train and storm backdrop live on the pin ROW (DreamTeamPin), not here.
import { useEffect, useState } from "react";
import { useTheme } from "../hooks/useTheme.js";
import "../dream-team-coven.css";
import "../deep-space-pin.css";

const HEADS = [
  "/spooky/head-skull.png",
  "/spooky/head-wizard.webp",
  "/spooky/head-witch.png",
  "/spooky/head-zombie.png",
  "/spooky/head-crow.png",
];

function CovenEmblem({ size }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setI((x) => (x + 1) % HEADS.length), 10000);
    return () => clearInterval(iv);
  }, []);
  const pad = Math.max(3, Math.round(size * 0.087));
  return (
    <span className="dt-coven" style={{ width: size, height: size, flexBasis: size, borderRadius: Math.round(size * 0.26) }}>
      <span className="dt-coven__glow" aria-hidden />
      <span className="dt-coven__smoke dt-coven__smoke--1" aria-hidden />
      <span className="dt-coven__smoke dt-coven__smoke--2" aria-hidden />
      {HEADS.map((src, idx) => (
        <span key={idx} aria-hidden className={`dt-coven__head ${idx === i ? "is-on" : ""}`}
              style={{ padding: pad, backgroundImage: `url(${src})` }} />
      ))}
    </span>
  );
}

// Deep Space "Crew" emblem — a morphing crew helmet (Commander → Astronaut → Alien → Rover → Comms)
// that flips-and-fades every 10s over a teal glow. Border satellites + sky live on the pin row.
const CREW = [
  { initial: "CM", color: "#ffb454" },
  { initial: "AN", color: "#35e0d0" },
  { initial: "LN", color: "#4fd18a" },
  { initial: "RV", color: "#5fa8ff" },
  { initial: "CV", color: "#c08bff" },
];
function Helmet({ color, initial }) {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%" aria-hidden>
      <ellipse cx="20" cy="21" rx="14" ry="15" fill="#dfe9f4" />
      <path d="M8 20 a12 12 0 0 1 24 0 v2 a12 9 0 0 1 -24 0 Z" fill={color} opacity="0.9" />
      <ellipse cx="20" cy="20" rx="9" ry="8" fill="#0a1220" />
      <ellipse cx="16.5" cy="17.5" rx="3" ry="3.4" fill={color} opacity="0.85" />
      <text x="20" y="35" textAnchor="middle" fontFamily="'JetBrains Mono',monospace" fontSize="7" fontWeight="700" fill="#dfe9f4">{initial}</text>
    </svg>
  );
}
function CrewEmblem({ size }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setI((x) => (x + 1) % CREW.length), 10000);
    return () => clearInterval(iv);
  }, []);
  const pad = Math.max(3, Math.round(size * 0.087));
  return (
    <span className="dt-crew" style={{ width: size, height: size, flexBasis: size, borderRadius: Math.round(size * 0.26) }}>
      <span className="dt-crew__glow" aria-hidden />
      {CREW.map((c, idx) => (
        <span key={idx} aria-hidden className={`dt-crew__head ${idx === i ? "is-on" : ""}`} style={{ padding: pad }}>
          <Helmet color={c.color} initial={c.initial} />
        </span>
      ))}
    </span>
  );
}

function AtomEmblem({ size }) {
  return (
    <span className="dt-logo" style={{ width: size, height: size, flexBasis: size }}>
      <svg className="dt-logo__svg" viewBox="0 0 48 48" fill="none">
        <defs>
          <radialGradient id="dt-core-grad" cx="50%" cy="45%" r="60%">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset=".55" stopColor="#eaf6ff" />
            <stop offset="1" stopColor="#7cc4ff" />
          </radialGradient>
        </defs>
        <g className="dt-orb dt-orb--1"><ellipse cx="24" cy="24" rx="18" ry="7.2" /><circle cx="42" cy="24" r="2.5" /></g>
        <g className="dt-orb dt-orb--2"><ellipse cx="24" cy="24" rx="18" ry="7.2" /><circle cx="42" cy="24" r="2.3" /></g>
        <g className="dt-orb dt-orb--3"><ellipse cx="24" cy="24" rx="18" ry="7.2" /><circle cx="42" cy="24" r="2.3" /></g>
        <circle className="dt-core-glow" cx="24" cy="24" r="6.5" />
        <circle className="dt-core" cx="24" cy="24" r="3.8" fill="url(#dt-core-grad)" />
      </svg>
    </span>
  );
}

export default function DreamTeamLogo({ size = 44 }) {
  const { theme } = useTheme();
  if (theme === "spooky") return <CovenEmblem size={size} />;
  if (theme === "deep") return <CrewEmblem size={size} />;
  return <AtomEmblem size={size} />;
}
